import React, { useState, useEffect, useCallback } from "react";
import { Database, ThumbsUp, ThumbsDown, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BACKEND_URL } from "@/lib/api";

function formatDatasetDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("nl-NL", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

/**
 * DatasetPanel shows relevant open datasets from data.overheid.nl based on
 * the societal theme. Designers can approve or reject datasets. Only approved
 * datasets are included as analysis context.
 *
 * Props:
 *   societalTheme   string, entered societal theme
 *   targetGroup     string, entered target group
 *   onDatasetsChange fn   , callback with the updated datasets array
 */
export function DatasetPanel({ societalTheme, targetGroup, onDatasetsChange, language = "en" }) {
  const t = language === "nl" ? {
    title: "Open datasets",
    description: "Relevante datasets op basis van je thema. Keur datasets goed of af, alleen goedgekeurde datasets gaan mee als context.",
    refresh: "Vernieuwen",
    error: "Open datasets konden niet worden opgehaald. Je kunt doorgaan zonder datasets of opnieuw proberen.",
    noTheme: "Vul eerst een maatschappelijk thema in om datasets te zoeken.",
    searching: "Datasets zoeken op data.overheid.nl...",
    noResults: "Geen datasets gevonden voor dit thema. Probeer een ander thema.",
    approved: "goedgekeurd",
    rejected: "afgekeurd",
    pending: "nog te beoordelen",
    includeNote: "(zonder goedkeuring worden geen open datasets meegenomen)",
    updated: "Bijgewerkt",
    view: "Bekijk dataset",
    approve: "Goedkeuren, meenemen als context",
    reject: "Afwijzen, niet meenemen",
  } : {
    title: "Open datasets",
    description: "Relevant datasets based on your theme. Approve or reject them, only approved datasets will be included as context.",
    refresh: "Refresh",
    error: "Could not fetch open datasets. You can continue without them or try Refresh.",
    noTheme: "Enter a Societal Theme first to search for datasets.",
    searching: "Searching datasets on data.overheid.nl...",
    noResults: "No datasets found for this theme. Try another theme.",
    approved: "approved",
    rejected: "rejected",
    pending: "still to review",
    includeNote: "(without approval, no open datasets are included)",
    updated: "Updated",
    view: "View dataset",
    approve: "Approve, include as context",
    reject: "Reject, do not include",
  };
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const fetchDatasets = useCallback(async () => {
    if (!societalTheme?.trim()) return;
    setLoading(true);
    setError(null);
    let timeoutId;
    try {
      const params = new URLSearchParams({
        societal_theme: societalTheme,
        target_group: targetGroup || "",
      });
      const controller = new AbortController();
      timeoutId = window.setTimeout(() => controller.abort(), 8000);
      const res = await fetch(`${BACKEND_URL}/api/dataset-search?${params}`, {
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const withApproval = (data.results || []).map((d) => ({ ...d, approved: null }));
      setDatasets(withApproval);
      setSearched(true);
      onDatasetsChange?.(withApproval);
    } catch (e) {
      setError(t.error);
      setSearched(true);
      setDatasets([]);
      onDatasetsChange?.([]);
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [societalTheme, targetGroup, onDatasetsChange, language]);

  // Automatically search when societalTheme changes
  useEffect(() => {
    if (societalTheme?.trim()) {
      fetchDatasets();
    }
  }, [societalTheme]); // eslint-disable-line

  const setApproval = (id, value) => {
    const updated = datasets.map((d) =>
      d.id === id ? { ...d, approved: d.approved === value ? null : value } : d
    );
    setDatasets(updated);
    onDatasetsChange?.(updated);
  };

  const approvedCount = datasets.filter((d) => d.approved === true).length;
  const rejectedCount = datasets.filter((d) => d.approved === false).length;
  const pendingCount = datasets.filter((d) => d.approved === null).length;

  return (
    <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid="synberry-dataset-panel">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[rgba(97,203,163,0.18)] text-[#61CBA3]">
              <Database className="h-4 w-4" />
            </span>
            <div className="font-display text-lg font-semibold text-black">
              6. {t.title}
            </div>
            <Badge className="rounded-full border border-black/15 text-white text-[10px]">
              data.overheid.nl
            </Badge>
          </div>
          <div className="mt-1 text-xs text-black/60">
            {t.description}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-xl"
          onClick={fetchDatasets}
          disabled={loading || !societalTheme?.trim()}
        >
          {loading
            ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          {t.refresh}
        </Button>
      </div>

      {/* Status summary */}
      {searched && !loading && datasets.length > 0 && (
        <div className="mt-2 text-xs text-black/60">
          {approvedCount > 0 && (
            <span className="text-[#61CBA3] font-medium">{approvedCount} {t.approved} </span>
          )}
          {rejectedCount > 0 && (
            <span className="text-red-400 font-medium">{rejectedCount} {t.rejected} </span>
          )}
          {pendingCount > 0 && (
            <span>{pendingCount} {t.pending}</span>
          )}
          {approvedCount === 0 && pendingCount > 0 && (
            <span className="ml-1 text-black/40">{t.includeNote}</span>
          )}
        </div>
      )}

      {/* No theme entered */}
      {!societalTheme?.trim() && (
        <div className="mt-3 text-sm text-black/50 italic">
          {t.noTheme}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="mt-3 flex items-center gap-2 text-sm text-black/60">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t.searching}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-3 text-sm text-red-500">{error}</div>
      )}

      {/* No results */}
      {searched && !loading && datasets.length === 0 && !error && (
        <div className="mt-3 text-sm text-black/50">
          {t.noResults}
        </div>
      )}

      {/* Dataset cards */}
      {datasets.length > 0 && (
        <div className="mt-3 space-y-2">
          {datasets.map((d) => (
            <div
              key={d.id}
              className={`rounded-xl border p-3 transition-colors ${
                d.approved === true
                  ? "border-[#61CBA3] bg-[rgba(97,203,163,0.06)]"
                  : d.approved === false
                  ? "border-red-200 bg-red-50/40 opacity-60"
                  : "border-black/10"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-black leading-snug">
                    {d.title}
                  </div>
                  <div className="text-xs text-black/50 mt-0.5">
                    {d.organization}
                    {formatDatasetDate(d.modifiedAt) && (
                      <span className="ml-1 text-black/40">, {t.updated} {formatDatasetDate(d.modifiedAt)}</span>
                    )}
                  </div>
                  {d.description && (
                    <div className="mt-1 text-xs text-black/60 line-clamp-2">
                      {d.description}
                    </div>
                  )}
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    {d.url && (
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-[#61CBA3] underline underline-offset-4"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {t.view}
                      </a>
                    )}
                    {d.downloadUrls?.length > 0 && (
                      <span className="text-xs text-black/40">
                        {d.downloadUrls.map((u) => u.format).join(", ")}
                      </span>
                    )}
                  </div>
                </div>

                {/* Thumbs up / down */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setApproval(d.id, true)}
                    className={`rounded-lg p-1.5 transition-colors ${
                      d.approved === true
                        ? "bg-[#61CBA3] text-white"
                        : "bg-black/5 text-black/40 hover:bg-[rgba(97,203,163,0.18)] hover:text-[#61CBA3]"
                    }`}
                    title={t.approve}
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setApproval(d.id, false)}
                    className={`rounded-lg p-1.5 transition-colors ${
                      d.approved === false
                        ? "bg-red-400 text-white"
                        : "bg-black/5 text-black/40 hover:bg-red-50 hover:text-red-400"
                    }`}
                    title={t.reject}
                  >
                    <ThumbsDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
